import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CreditCard, CheckCircle, Shield, Award, Zap, Crown, Loader2, Globe, Mail, Smartphone, Wallet, QrCode, X, AlertTriangle, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// GST Rate: 18% (9% CGST + 9% SGST)
const GST_RATE = 0.18;
const CGST_RATE = 0.09;
const SGST_RATE = 0.09;

// Calculate GST breakdown from base price
const calculateGST = (basePrice) => {
  const cgst = Math.round(basePrice * CGST_RATE);
  const sgst = Math.round(basePrice * SGST_RATE);
  const totalGst = cgst + sgst;
  const total = basePrice + totalGst;
  return { cgst, sgst, totalGst, total };
};

const PROFESSIONAL_TIERS = [
  {
    id: 'express',
    name: 'Express',
    pages: 2,
    questions: 20,
    usd: 111,
    inr: 12999,
    ...calculateGST(12999),
    features: ['Quick Leadership Snapshot', 'Core Competencies', 'Executive Summary'],
    popular: false,
    isExpress: true,
  },
  {
    id: 'basic',
    name: 'Basic',
    pages: 3,
    questions: 65,
    usd: 124,
    inr: 22999,
    ...calculateGST(22999),
    features: ['Leadership Matrix', 'Core Competencies', 'Executive Summary'],
    popular: false,
  },
  {
    id: 'detailed',
    name: 'Detailed',
    pages: 10,
    questions: 65,
    usd: 249,
    inr: 34999,
    ...calculateGST(34999),
    features: ['Everything in Basic', 'Cognitive Style Analysis', 'Behavioral Archetypes', 'Decision Architecture', 'Behavioral Patterns'],
    popular: true,
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive',
    pages: 40,
    questions: 65,
    usd: 624,
    inr: 51999,
    ...calculateGST(51999),
    features: ['Everything in Detailed', '22 Evaluation Frameworks', 'AI-Powered Insights', 'Role-Fit Analysis', '90/180/365 Day Roadmap', 'Success Mantra'],
    popular: false,
  },
];

const STUDENT_TIERS = [
  {
    id: 'express',
    name: 'Express',
    pages: 2,
    questions: 20,
    usd: 31,
    inr: 2499,
    ...calculateGST(2499),
    features: ['Quick Career Snapshot', 'Interest Profile', 'Summary'],
    popular: false,
    isExpress: true,
  },
  {
    id: 'basic',
    name: 'Basic',
    pages: 3,
    questions: 55,
    usd: 35,
    inr: 4299,
    ...calculateGST(4299),
    features: ['Interest Profile', 'Career Suggestions', 'Summary Report'],
    popular: false,
  },
  {
    id: 'detailed',
    name: 'Detailed',
    pages: 10,
    questions: 55,
    usd: 59,
    inr: 5799,
    ...calculateGST(5799),
    features: ['Everything in Basic', 'Aptitude Analysis', 'Life Style Grid', 'Personality Insights'],
    popular: true,
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive',
    pages: 35,
    questions: 55,
    usd: 119,
    inr: 8299,
    ...calculateGST(8299),
    features: ['Everything in Detailed', 'Career Path Mapping', 'Strengths Analysis', 'Development Plan', 'Industry Recommendations', 'AI Career Buddy (Aryan)'],
    popular: false,
  },
];

export const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedTier, setSelectedTier] = useState('detailed');
  const [currency, setCurrency] = useState('inr');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [userTypeConfirmed, setUserTypeConfirmed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' or 'stripe'
  
  // Debug log - IMPORTANT for troubleshooting
  console.log('PaymentPage - user:', user?.user_type, 'is_paid:', user?.is_paid, 'authLoading:', authLoading, 'email:', user?.email);
  
  // CRITICAL: Determine user type with explicit check
  const isStudent = user?.user_type === 'student';
  const tiers = isStudent ? STUDENT_TIERS : PROFESSIONAL_TIERS;

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
    // If user already paid, redirect to assessment
    if (user?.is_paid) {
      navigate('/instructions');
    }
    // Set confirmed once user data is loaded
    if (!authLoading && user) {
      setUserTypeConfirmed(true);
    }
  }, [user, authLoading, navigate]);

  // CRITICAL: Show loading while auth is loading OR user data not yet available
  if (authLoading || !user || !userTypeConfirmed) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#CC5500] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your pricing...</p>
        </div>
      </div>
    );
  }

  // Show warning if user came from student page but has professional account
  const showUserTypeMismatchWarning = () => {
    const referrer = document.referrer;
    const fromStudentPage = referrer.includes('/login/student');
    return fromStudentPage && !isStudent;
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Redirecting to assessment...');
        // Update user state and redirect
        setTimeout(() => navigate('/instructions'), 1500);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // Razorpay Payment Handler
  const handleRazorpayPayment = async () => {
    setLoading(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      // Create Razorpay order
      const orderResponse = await axios.post(`${API}/razorpay/create-order`, {
        tier: selectedTier,
        currency: 'inr',
      });

      const { order_id, amount, key_id, prefill, notes } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "LeadEconomics™",
        description: `${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Assessment Report`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyResponse = await axios.post(`${API}/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              toast.success('Payment successful! Redirecting to assessment...');
              setTimeout(() => navigate('/instructions'), 1500);
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: prefill,
        notes: notes,
        theme: {
          color: "#CC5500",
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });
      razorpayInstance.open();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Stripe Payment Handler
  const handleStripePayment = async () => {
    setLoading(true);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payment/create-checkout`, {
        tier: selectedTier,
        currency: currency,
        origin_url: originUrl,
      });

      if (response.data.url) {
        try {
          window.location.assign(response.data.url);
        } catch (e) {
          window.location.href = response.data.url;
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Main payment handler
  const handlePayment = async () => {
    if (currency === 'inr' && paymentMethod === 'razorpay') {
      await handleRazorpayPayment();
    } else {
      await handleStripePayment();
    }
  };

  // Generate UPI QR code data
  const generateUPIQRData = () => {
    const tier = tiers.find(t => t.id === selectedTier);
    const amount = tier?.inr || 0;
    // UPI deep link format for QR code
    const upiId = 'leadeconomics@icici'; // Replace with actual UPI ID
    const payeeName = 'LeadEconomics';
    const transactionNote = `LeadEconomics ${tier?.name} Report`;
    return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
  };

  const formatPrice = (tier) => {
    if (currency === 'inr') {
      // Show total including GST
      return `₹${tier.total.toLocaleString('en-IN')}`;
    }
    return `$${tier.usd}`;
  };

  const formatBasePrice = (tier) => {
    if (currency === 'inr') {
      return `₹${tier.inr.toLocaleString('en-IN')}`;
    }
    return `$${tier.usd}`;
  };

  const formatCGST = (tier) => {
    if (currency === 'inr' && tier.cgst) {
      return `₹${tier.cgst.toLocaleString('en-IN')}`;
    }
    return null;
  };

  const formatSGST = (tier) => {
    if (currency === 'inr' && tier.sgst) {
      return `₹${tier.sgst.toLocaleString('en-IN')}`;
    }
    return null;
  };

  const formatTotalGST = (tier) => {
    if (currency === 'inr' && tier.totalGst) {
      return `₹${tier.totalGst.toLocaleString('en-IN')}`;
    }
    return null;
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#CC5500] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
          <p className="text-gray-400">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="payment-page">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <nav className="border-b border-white/10 bg-[#0D0D0D]/80 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <h1 className="text-xl font-heading font-bold text-white">
                Lead<span className="text-[#CC5500]">Economics</span>™
              </h1>
              {/* Show user type badge */}
              <div className="flex items-center gap-2">
                {user?.is_admin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await axios.post(`${API}/demo/bypass-payment`, 
                          { tier: selectedTier },
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                        );
                        toast.success('Demo mode activated! Redirecting to assessment...');
                        // Update local user state
                        setTimeout(() => {
                          window.location.href = '/instructions';
                        }, 1000);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to activate demo mode');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="border-green-500 text-green-500 hover:bg-green-500/10"
                    data-testid="demo-mode-btn"
                  >
                    <Zap className="mr-1 h-3 w-3" />
                    Demo Mode (Skip Payment)
                  </Button>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isStudent 
                    ? 'bg-[#C5A065]/20 text-[#C5A065] border border-[#C5A065]/30' 
                    : 'bg-[#CC5500]/20 text-[#CC5500] border border-[#CC5500]/30'
                }`} data-testid="user-type-badge">
                  {isStudent ? 'Student' : 'Professional'}
                </span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Student Confirmation */}
          {isStudent && (
            <div className="mb-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <div>
                  <p className="text-green-400 text-sm">
                    <strong>Student Assessment:</strong> Choose your Career Discovery report tier
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Professional Confirmation */}
          {!isStudent && (
            <div className="mb-8 p-4 bg-[#CC5500]/10 border border-[#CC5500]/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Award className="h-6 w-6 text-[#CC5500]" />
                <div>
                  <p className="text-[#CC5500] text-sm">
                    <strong>Leadership Assessment:</strong> Choose your Executive Intelligence report tier
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="text-center mb-12">
            {/* User Type Confirmation Banner */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isStudent 
                ? 'bg-[#C5A065]/20 border border-[#C5A065]/30' 
                : 'bg-[#CC5500]/20 border border-[#CC5500]/30'
            }`}>
              <span className={`text-sm font-medium ${isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'}`}>
                {isStudent ? '🎓 Student Assessment Pricing' : '💼 Professional Assessment Pricing'}
              </span>
            </div>
            
            <h2 className="text-4xl font-heading font-bold text-white mb-4">
              Choose Your Report Tier
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {isStudent 
                ? 'Select the report depth that best suits your career discovery needs.'
                : 'Select the level of leadership intelligence that matches your requirements.'}
            </p>

            {/* Currency Toggle */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => setCurrency('inr')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                  currency === 'inr'
                    ? 'bg-[#CC5500]/20 border-[#CC5500] text-[#CC5500]'
                    : 'border-white/20 text-gray-400 hover:border-white/40'
                }`}
                data-testid="currency-inr"
              >
                <IndianRupee className="h-4 w-4" />
                ₹ INR
              </button>
              <button
                onClick={() => setCurrency('usd')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                  currency === 'usd'
                    ? 'bg-[#CC5500]/20 border-[#CC5500] text-[#CC5500]'
                    : 'border-white/20 text-gray-400 hover:border-white/40'
                }`}
                data-testid="currency-usd"
              >
                <Globe className="h-4 w-4" />
                $ USD
              </button>
            </div>

            {/* Payment Method Toggle - Only for INR */}
            {currency === 'inr' && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <span className="text-sm text-gray-500">Pay with:</span>
                <button
                  onClick={() => setPaymentMethod('razorpay')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    paymentMethod === 'razorpay'
                      ? 'bg-[#3395FF]/20 border-[#3395FF] text-[#3395FF]'
                      : 'border-white/20 text-gray-400 hover:border-white/40'
                  }`}
                  data-testid="payment-razorpay"
                >
                  <Wallet className="h-4 w-4" />
                  Razorpay (UPI/Cards)
                </button>
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    paymentMethod === 'stripe'
                      ? 'bg-[#635BFF]/20 border-[#635BFF] text-[#635BFF]'
                      : 'border-white/20 text-gray-400 hover:border-white/40'
                  }`}
                  data-testid="payment-stripe"
                >
                  <CreditCard className="h-4 w-4" />
                  Stripe (Cards)
                </button>
              </div>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className={`relative cursor-pointer rounded-2xl p-5 transition-all duration-300 ${
                  selectedTier === tier.id
                    ? 'bg-gradient-to-br from-[#CC5500]/20 to-[#C5A065]/10 border-2 border-[#CC5500] scale-[1.02]'
                    : 'bg-white/5 border-2 border-white/10 hover:border-white/20'
                }`}
                data-testid={`tier-${tier.id}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#CC5500] to-[#C5A065] text-white text-xs font-medium rounded-full">
                      <Crown className="h-3 w-3" />
                      Most Popular
                    </span>
                  </div>
                )}
                
                {tier.isExpress && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#4A90A4] to-[#50C878] text-white text-xs font-medium rounded-full">
                      <Zap className="h-3 w-3" />
                      Quick Screen
                    </span>
                  </div>
                )}

                <div className="text-center mb-4 pt-2">
                  <h3 className="text-lg font-bold text-white mb-1">{tier.name}</h3>
                  <p className="text-xs text-gray-500">{tier.pages}-Page Report • {tier.questions} Questions</p>
                </div>

                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-white">{formatPrice(tier)}</span>
                  {currency === 'inr' && tier.cgst && (
                    <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                      <div className="flex justify-between px-2">
                        <span className="text-gray-600">Base Price:</span>
                        <span className="text-gray-400">{formatBasePrice(tier)}</span>
                      </div>
                      <div className="flex justify-between px-2">
                        <span className="text-amber-500/70">CGST (9%):</span>
                        <span className="text-amber-500/80">{formatCGST(tier)}</span>
                      </div>
                      <div className="flex justify-between px-2">
                        <span className="text-amber-500/70">SGST (9%):</span>
                        <span className="text-amber-500/80">{formatSGST(tier)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-4">
                  {tier.features.slice(0, 4).map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-xs text-gray-400">
                      <CheckCircle className="h-3 w-3 text-[#CC5500] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className={`w-full h-1 rounded-full ${
                  selectedTier === tier.id 
                    ? 'bg-gradient-to-r from-[#CC5500] to-[#C5A065]' 
                    : 'bg-white/10'
                }`} />
              </div>
            ))}
          </div>

          {/* Order Summary with GST Breakdown */}
          {currency === 'inr' && (
            <div className="mt-10 max-w-md mx-auto" data-testid="order-summary">
              <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl border border-white/10 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <IndianRupee className="h-5 w-5 text-[#CC5500]" />
                  Order Summary
                </h3>
                
                {(() => {
                  const tier = tiers.find(t => t.id === selectedTier);
                  if (!tier) return <p className="text-gray-400">Select a plan to see pricing</p>;
                  return (
                    <div className="space-y-3">
                      {/* Tier Name */}
                      <div className="flex justify-between items-center pb-3 border-b border-white/10">
                        <span className="text-gray-400">Report Type</span>
                        <span className="text-white font-medium">{tier.name} ({tier.pages} Pages)</span>
                      </div>
                      
                      {/* Base Price */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Base Price</span>
                        <span className="text-white">₹{tier.inr?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      
                      {/* CGST */}
                      <div className="flex justify-between items-center">
                        <span className="text-amber-500/80">CGST @ 9%</span>
                        <span className="text-amber-500">₹{tier.cgst?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      
                      {/* SGST */}
                      <div className="flex justify-between items-center">
                        <span className="text-amber-500/80">SGST @ 9%</span>
                        <span className="text-amber-500">₹{tier.sgst?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      
                      {/* Divider */}
                      <div className="border-t border-white/10 pt-3">
                        {/* Total GST */}
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-500 text-sm">Total GST (18%)</span>
                          <span className="text-amber-500/80 text-sm">₹{tier.totalGst?.toLocaleString('en-IN') || '0'}</span>
                        </div>
                        
                        {/* Grand Total */}
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold text-lg">Total Amount</span>
                          <span className="text-[#CC5500] font-bold text-2xl">₹{tier.total?.toLocaleString('en-IN') || '0'}</span>
                        </div>
                      </div>
                      
                      {/* GST Number */}
                      <div className="mt-4 pt-3 border-t border-white/10 text-center">
                        <p className="text-xs text-gray-500">
                          GSTIN: <span className="text-gray-400 font-mono">27AALCS7382R2Z6</span>
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          S3 Global Human Capital Solution
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Payment Button */}
          <div className="mt-8 text-center">
            <Button
              onClick={handlePayment}
              disabled={loading}
              className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white px-12 py-7 text-lg"
              data-testid="pay-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-5 w-5" />
                  Pay {formatPrice(tiers.find(t => t.id === selectedTier))} & Begin Assessment
                </>
              )}
            </Button>

            {/* UPI Payment Options - Only show for INR */}
            {currency === 'inr' && (
              <div className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Smartphone className="h-5 w-5 text-[#C5A065]" />
                  <span className="text-sm font-medium text-white">Pay with UPI</span>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 hover:border-[#CC5500]/50 transition-all cursor-pointer" data-testid="upi-gpay">
                    <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-500">G</span>
                    </div>
                    <span className="text-sm text-gray-300">GPay</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 hover:border-[#CC5500]/50 transition-all cursor-pointer" data-testid="upi-phonepe">
                    <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-white">P</span>
                    </div>
                    <span className="text-sm text-gray-300">PhonePe</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 hover:border-[#CC5500]/50 transition-all cursor-pointer" data-testid="upi-paytm">
                    <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-white">₱</span>
                    </div>
                    <span className="text-sm text-gray-300">Paytm</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg border border-white/20 hover:border-[#CC5500]/50 transition-all cursor-pointer" data-testid="upi-bhim">
                    <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-white">B</span>
                    </div>
                    <span className="text-sm text-gray-300">BHIM</span>
                  </div>
                </div>
                
                {/* QR Code Button */}
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setShowQR(!showQR)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#CC5500]/20 to-[#C5A065]/20 rounded-lg border border-[#CC5500]/30 hover:border-[#CC5500]/60 transition-all"
                    data-testid="show-qr-button"
                  >
                    <QrCode className="h-5 w-5 text-[#CC5500]" />
                    <span className="text-sm text-white">{showQR ? 'Hide' : 'Show'} UPI QR Code</span>
                  </button>
                </div>
                
                {/* QR Code Display */}
                {showQR && (
                  <div className="mt-6 p-6 bg-white rounded-xl text-center">
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Scan to pay</p>
                      <p className="text-2xl font-bold text-gray-900">{formatPrice(tiers.find(t => t.id === selectedTier))}</p>
                      {currency === 'inr' && tiers.find(t => t.id === selectedTier)?.cgst && (
                        <div className="mt-2 text-xs text-gray-500 space-y-1">
                          <div className="flex justify-center gap-4">
                            <span>Base: {formatBasePrice(tiers.find(t => t.id === selectedTier))}</span>
                          </div>
                          <div className="flex justify-center gap-4">
                            <span className="text-amber-600">CGST (9%): {formatCGST(tiers.find(t => t.id === selectedTier))}</span>
                            <span className="text-amber-600">SGST (9%): {formatSGST(tiers.find(t => t.id === selectedTier))}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* QR Code Image - Using a QR code API */}
                    <div className="flex justify-center mb-4">
                      <img 
                        key={`qr-${selectedTier}-${tiers.find(t => t.id === selectedTier)?.total}`}
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`upi://pay?pa=leadeconomics@icici&pn=LeadEconomics&am=${tiers.find(t => t.id === selectedTier)?.total || 0}&cu=INR&tn=LeadEconomics%20${selectedTier}%20Report`)}`}
                        alt="UPI QR Code"
                        className="w-48 h-48 border-4 border-[#CC5500] rounded-lg"
                        data-testid="upi-qr-code"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mb-2">UPI ID: leadeconomics@icici</p>
                    <p className="text-xs text-gray-400">Scan with any UPI app to pay</p>
                    <p className="text-xs text-green-600 font-medium mt-2">
                      Amount: ₹{(tiers.find(t => t.id === selectedTier)?.total || 0).toLocaleString('en-IN')} (Incl. 18% GST)
                    </p>
                    <p className="text-xs text-gray-400 mt-1">GST No: 27AALCS7382R2Z6</p>
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-xs text-yellow-700">
                        <strong>Note:</strong> After payment, please send payment screenshot to <a href="mailto:report@leadeconomics.in" className="text-[#CC5500] underline">report@leadeconomics.in</a> for verification
                      </p>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-4">
                  All UPI apps supported via Stripe's secure payment gateway
                </p>
              </div>
            )}

            {/* Career Consultation / Mentoring CTA */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[#1a1a2e] to-[#16213e] rounded-2xl border border-white/10">
              {isStudent ? (
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">Need Personalized Guidance?</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Book a 1-on-1 Career Consultation with our expert counselors
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="text-[#C5A065]">
                      <span className="text-2xl font-bold">₹50,000+</span>
                      <span className="text-sm text-gray-400 ml-2">+ applicable taxes</span>
                    </div>
                    <span className="text-gray-500 hidden sm:inline">|</span>
                    <div className="text-[#C5A065]">
                      <span className="text-2xl font-bold">$800+</span>
                      <span className="text-sm text-gray-400 ml-2">for international</span>
                    </div>
                  </div>
                  <a 
                    href="mailto:info@leadeconomics.in?subject=Career Consultation Inquiry" 
                    className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C5A065] to-[#CC5500] text-white font-medium rounded-xl hover:opacity-90 transition-all"
                    data-testid="book-consultation-cta"
                  >
                    <Mail className="h-5 w-5" />
                    Email for Details: info@leadeconomics.in
                  </a>
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">Executive Mentoring Sessions</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Premium 1-on-1 mentoring for C-suite executives and senior leaders
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="text-[#CC5500]">
                      <span className="text-2xl font-bold">₹1,25,000+</span>
                      <span className="text-sm text-gray-400 ml-2">+ taxes / hour</span>
                    </div>
                    <span className="text-gray-500 hidden sm:inline">|</span>
                    <div className="text-[#CC5500]">
                      <span className="text-2xl font-bold">$2,000+</span>
                      <span className="text-sm text-gray-400 ml-2">/ hour</span>
                    </div>
                  </div>
                  <a 
                    href="mailto:info@leadeconomics.in?subject=Executive Mentoring Inquiry" 
                    className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#CC5500] to-[#C5A065] text-white font-medium rounded-xl hover:opacity-90 transition-all"
                    data-testid="book-mentoring-cta"
                  >
                    <Mail className="h-5 w-5" />
                    Email for Details: info@leadeconomics.in
                  </a>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-1">
                <Wallet className="h-3 w-3" />
                <span>UPI / Cards / Netbanking</span>
              </div>
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                <span>Report via Email</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
